import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// The single canonical origin for the site. Every canonical URL is built from
// this, so Google always consolidates duplicates (www vs non-www, http vs
// https, junk query strings) onto one address.
//
// NOTE: the bare domain is canonical because the backend API is served from
// https://pianolang.com/api/*. Redirecting the bare domain to www would
// redirect API traffic too, so www -> bare is the safe direction.
export const CANONICAL_ORIGIN = "https://pianolang.com";

// Query params that genuinely change what the page shows and therefore belong
// in the canonical URL. Anything else (utm_*, fbclid, gclid, ...) is stripped
// so tracking links don't register as separate duplicate pages.
const MEANINGFUL_PARAMS = ["q", "page"];

/**
 * Build the canonical URL for a path + search string.
 * Normalises the path (lowercase host handled by origin, no trailing slash
 * except root) and keeps only params that affect the content.
 */
export function buildCanonicalUrl(pathname, search) {
  let path = pathname || "/";

  // Collapse duplicate slashes and drop a trailing slash so /ranking/ and
  // /ranking resolve to one canonical URL.
  path = path.replace(/\/{2,}/g, "/");
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  const incoming = new URLSearchParams(search || "");
  const kept = new URLSearchParams();
  MEANINGFUL_PARAMS.forEach((key) => {
    const value = incoming.get(key);
    // Skip empty values and page=1, which is the same content as no page param.
    if (value && !(key === "page" && value === "1")) {
      kept.set(key, value);
    }
  });

  const query = kept.toString();
  return CANONICAL_ORIGIN + path + (query ? `?${query}` : "");
}

/** Create or update a <link> in <head> and return nothing. */
function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Create or update a <meta> tag by name or property. */
function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (content === null || content === undefined) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Per-route <head> management: self-referencing canonical, title, description
 * and robots directives.
 *
 * @param {object}  options
 * @param {string}  [options.title]       Page title.
 * @param {string}  [options.description] Meta description.
 * @param {boolean} [options.noindex]     Keep the page out of the index.
 * @param {string}  [options.canonicalPath] Override the canonical path, e.g.
 *   point a variant at its preferred URL instead of itself.
 * @param {string}  [options.image]       og:image URL.
 */
export function useSeo({
  title,
  description,
  noindex = false,
  canonicalPath,
  image,
} = {}) {
  const location = useLocation();

  useEffect(() => {
    const canonical = canonicalPath
      ? CANONICAL_ORIGIN + canonicalPath
      : buildCanonicalUrl(location.pathname, location.search);

    setLink("canonical", canonical);

    if (title) {
      document.title = title;
      setMeta("property", "og:title", title);
    }

    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
    }

    setMeta("property", "og:url", canonical);
    setMeta("property", "og:type", "website");
    if (image) setMeta("property", "og:image", image);

    // noindex keeps login-walled and not-found pages out of the index; they
    // would otherwise look like duplicates of each other.
    if (noindex) {
      setMeta("name", "robots", "noindex, follow");
    } else {
      setMeta("name", "robots", "index, follow");
    }
  }, [
    location.pathname,
    location.search,
    title,
    description,
    noindex,
    canonicalPath,
    image,
  ]);
}

export default useSeo;
